import type {
  AdminListItem,
  AuditLogItem,
  DashboardRecent,
  DashboardSummary,
  FinanceSummary,
  GlobalSearchResult,
  GrantSubscriptionResponse,
  InfraDonorItem,
  InfraJobItem,
  InfraJobStarted,
  InfraLocationItem,
  InfraImportResult,
  InfraNodeItem,
  PaginatedAuditLog,
  PaginatedOvpnKeys,
  PaginatedPayments,
  PaginatedPromoRedemptions,
  PaginatedSupportTickets,
  PaginatedSupportStorageFiles,
  PaginatedUsers,
  PaginatedVpnKeys,
  OvpnKeyConfigResponse,
  OvpnKeyDetail,
  OvpnSecretImportResult,
  OvpnServerHealth,
  OvpnServerListItem,
  PaymentDetail,
  PromoDetail,
  PromoListItem,
  TariffDetail,
  TariffListItem,
  ReferralRewardRuleItem,
  ServerHealth,
  ServerListItem,
  SettingsOverview,
  SupportStorageDeleteResult,
  SupportStorageStats,
  SupportTicketDetail,
  SyncResult,
  UserDetail,
  VpnKeyConfigResponse,
  VpnKeyDetail,
} from './types';

const API_PREFIX = import.meta.env.VITE_API_PREFIX || '/vpnbotpanel/api';

export interface ApiError {
  code: string;
  message: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: ApiError;
}

export interface UserProfile {
  telegram_user_id: number;
  role: string;
  username?: string;
  first_name?: string;
  photo_url?: string;
}

export interface AuthMeResponse {
  authenticated: boolean;
  user?: UserProfile;
  csrf_token?: string;
  session_id?: string;
}

export interface HealthStatus {
  status: string;
  database: string;
  redis: string;
  version: string;
}

export interface PublicConfig {
  admin_panel_url: string;
  user_portal_url: string;
  app_base_path: string;
  csrf_header_name: string;
  bot_username: string;
}

export type {
  AdminListItem,
  AuditLogItem,
  DashboardRecent,
  DashboardSummary,
  FinanceSummary,
  GlobalSearchResult,
  GrantSubscriptionResponse,
  PaginatedAuditLog,
  PaginatedOvpnKeys,
  PaginatedPayments,
  PaginatedPromoRedemptions,
  PaginatedSupportTickets,
  PaginatedUsers,
  PaginatedVpnKeys,
  OvpnKeyConfigResponse,
  OvpnKeyDetail,
  OvpnServerListItem,
  PaymentDetail,
  PromoDetail,
  PromoListItem,
  TariffDetail,
  TariffListItem,
  ReferralRewardRuleItem,
  ServerHealth,
  ServerListItem,
  SettingsOverview,
  SupportTicketDetail,
  SyncResult,
  UserDetail,
  VpnKeyConfigResponse,
  VpnKeyDetail,
};

const SESSION_STORAGE_KEY = 'yp_admin_session_id';

let csrfToken: string | null = null;
let adminSessionId: string | null =
  typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(SESSION_STORAGE_KEY) : null;

export function getCsrfToken(): string | null {
  return csrfToken;
}

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

export function setAdminSessionId(id: string | null): void {
  adminSessionId = id;
  if (id) sessionStorage.setItem(SESSION_STORAGE_KEY, id);
  else sessionStorage.removeItem(SESSION_STORAGE_KEY);
}

export function getAdminSessionId(): string | null {
  return adminSessionId;
}

export function applyAdminAuth(data: AuthMeResponse | undefined): void {
  if (data?.authenticated) {
    setCsrfToken(data.csrf_token ?? null);
    if (data.session_id) setAdminSessionId(data.session_id);
  } else {
    setCsrfToken(null);
    setAdminSessionId(null);
  }
}

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<ApiResponse<T>> {
  const method = (options.method ?? 'GET').toUpperCase();
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  if (options.body && !isFormData && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  if (csrfToken && method !== 'GET' && method !== 'HEAD') {
    headers['X-CSRF-Token'] = csrfToken;
  }

  const response = await fetch(`${API_PREFIX}${path}`, {
    ...options,
    method,
    headers,
    credentials: 'include',
    cache: 'no-store',
  });

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    if (response.status === 413) {
      return {
        success: false,
        error: {
          code: 'file_too_large',
          message:
            'Файл слишком большой (413). В nginx нужен client_max_body_size 55m в server{} (и в location API), затем nginx -t && systemctl reload nginx. Лимит API — 50 MB.',
        },
      };
    }
    return {
      success: false,
      error: {
        code: 'invalid_response',
        message: `API error (${response.status}): ${response.statusText || 'non-JSON response'}`,
      },
    };
  }

  const payload: unknown = await response.json();

  if (!response.ok) {
    if (payload && typeof payload === 'object' && payload !== null) {
      const body = payload as Record<string, unknown>;
      if (body.error && typeof body.error === 'object') {
        return { success: false, error: body.error as ApiError };
      }
      const detail = body.detail;
      if (detail && typeof detail === 'object' && detail !== null && 'code' in detail && 'message' in detail) {
        return {
          success: false,
          error: {
            code: String((detail as ApiError).code),
            message: String((detail as ApiError).message),
          },
        };
      }
      if (typeof detail === 'string') {
        return { success: false, error: { code: 'http_error', message: detail } };
      }
    }
    if (response.status === 401) {
      return { success: false, error: { code: 'not_authenticated', message: 'Unauthorized' } };
    }
    return {
      success: false,
      error: { code: 'http_error', message: `Server error (${response.status})` },
    };
  }

  if (payload && typeof payload === 'object' && payload !== null && 'success' in payload) {
    return payload as ApiResponse<T>;
  }

  return { success: true, data: payload as T };
}

function qs(params: Record<string, string | number | undefined | null>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export const api = {
  getConfig: () => request<PublicConfig>('/auth/config'),
  getMe: () => request<AuthMeResponse>('/auth/me'),
  loginTelegram: (payload: Record<string, unknown>) =>
    request<AuthMeResponse>('/auth/telegram', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  loginTelegramWebApp: (init_data: string) =>
    request<AuthMeResponse>('/auth/telegram-webapp', {
      method: 'POST',
      body: JSON.stringify({ init_data }),
    }),
  logout: () => request<{ logged_out: boolean }>('/auth/logout', { method: 'POST' }),
  getHealth: () => request<HealthStatus>('/health'),

  getDashboardSummary: () => request<DashboardSummary>('/dashboard/summary'),
  getDashboardRecent: (limit = 10) =>
    request<DashboardRecent>(`/dashboard/recent${qs({ limit })}`),

  listUsers: (params: {
    page?: number;
    page_size?: number;
    q?: string;
    subscription_status?: string;
    tier?: string;
    language?: string;
  }) => request<PaginatedUsers>(`/users${qs(params)}`),

  getUser: (userId: number) => request<UserDetail>(`/users/${userId}`),

  globalSearch: (q: string, limit = 20) =>
    request<GlobalSearchResult>(`/search${qs({ q, limit })}`),

  listVpnKeys: (params: {
    page?: number;
    page_size?: number;
    user_id?: number;
    server_code?: string;
    status?: string;
    q?: string;
  }) => request<PaginatedVpnKeys>(`/vpn/keys${qs(params)}`),

  getVpnKey: (keyId: number, live = true) =>
    request<VpnKeyDetail>(`/vpn/keys/${keyId}${qs({ live: live ? 1 : 0 })}`),

  getVpnKeyConfig: (keyId: number) =>
    request<VpnKeyConfigResponse>(`/vpn/keys/${keyId}/config`),

  createVpnKey: (payload: { user_id: number; server_code: string; force?: boolean }) =>
    request<{ key_id: number; client_id: string; server_code: string }>('/vpn/keys', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  importWgOrphans: (payload: { server_code: string; dry_run?: boolean }) =>
    request<{
      server_code: string;
      dry_run: boolean;
      to_import: number;
      already_ok: number;
      skipped_bad_name: number;
      imported: number;
      errors: string[];
      samples: Array<{ user_id: number; client_id: string; name: string; key_id?: number }>;
    }>('/vpn/import-orphans', {
      method: 'POST',
      body: JSON.stringify({ dry_run: true, ...payload }),
    }),

  deleteVpnKey: (keyId: number) =>
    request<{ deleted: boolean; key_id: number }>(`/vpn/keys/${keyId}`, { method: 'DELETE' }),

  listOvpnKeys: (params: {
    page?: number;
    page_size?: number;
    user_id?: number;
    server_code?: string;
    status?: string;
    q?: string;
  }) => request<PaginatedOvpnKeys>(`/ovpn/keys${qs(params)}`),

  getOvpnKey: (keyId: number) => request<OvpnKeyDetail>(`/ovpn/keys/${keyId}`),

  getOvpnKeyConfig: (keyId: number) =>
    request<OvpnKeyConfigResponse>(`/ovpn/keys/${keyId}/config`),

  listOvpnServers: () => request<OvpnServerListItem[]>('/ovpn/servers'),

  updateOvpnServer: (serverCode: string, payload: Record<string, unknown>) =>
    request<OvpnServerListItem>(`/ovpn/servers/${encodeURIComponent(serverCode)}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),

  getOvpnServerHealth: (serverCode: string) =>
    request<OvpnServerHealth>(`/ovpn/servers/${encodeURIComponent(serverCode)}/health`),

  importOvpnSecrets: () =>
    request<OvpnSecretImportResult>('/ovpn/servers/import-secrets', {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  deleteOvpnKey: (keyId: number) =>
    request<{ deleted: boolean; key_id: number }>(`/ovpn/keys/${keyId}`, { method: 'DELETE' }),

  listServers: () => request<ServerListItem[]>('/servers'),

  getServerHealth: (code: string) => request<ServerHealth>(`/servers/${encodeURIComponent(code)}/health`),

  reloadServersConfig: () =>
    request<{ reloaded: boolean; servers_count: number; ovpn_servers_count?: number }>(
      '/servers/reload-config',
      {
        method: 'POST',
        body: JSON.stringify({}),
      },
    ),

  syncServerReplicas: (code: string) =>
    request<SyncResult>(`/servers/${encodeURIComponent(code)}/sync`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  listInfraNodes: () => request<InfraNodeItem[]>('/servers/nodes'),

  listInfraLocations: () => request<InfraLocationItem[]>('/servers/nodes/locations'),

  importInfraFromWgServers: () =>
    request<InfraImportResult>('/servers/nodes/import-wg-servers', {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  createInfraNode: (payload: {
    host: string;
    ssh_port?: number;
    ssh_login: string;
    ssh_password: string;
    location: string;
    node_number: number;
    domain?: string;
    base_url?: string;
  }) =>
    request<InfraNodeItem>('/servers/nodes', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  updateInfraNode: (id: number, payload: Record<string, unknown>) =>
    request<InfraNodeItem>(`/servers/nodes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),

  deleteInfraNode: (id: number) =>
    request<{ deleted: boolean; id: number }>(`/servers/nodes/${id}`, { method: 'DELETE' }),

  getInfraJob: (jobId: number) => request<InfraJobItem>(`/servers/nodes/jobs/${jobId}`),

  getInfraActiveJob: (nodeId: number) =>
    request<InfraJobItem | null>(`/servers/nodes/${nodeId}/active-job`),

  listInfraDonors: (nodeId: number) =>
    request<InfraDonorItem[]>(`/servers/nodes/${nodeId}/donors`),

  checkInfraNode: (nodeId: number) =>
    request<InfraJobStarted>(`/servers/nodes/${nodeId}/check`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  refreshInfraNode: (nodeId: number) =>
    request<InfraJobStarted>(`/servers/nodes/${nodeId}/refresh`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  provisionInfraNode: (nodeId: number) =>
    request<InfraJobStarted>(`/servers/nodes/${nodeId}/provision`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  installWgInfraNode: (nodeId: number) =>
    request<InfraJobStarted>(`/servers/nodes/${nodeId}/install-wg`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  syncInfraNode: (nodeId: number, donorNodeId: number) =>
    request<InfraJobStarted>(`/servers/nodes/${nodeId}/sync`, {
      method: 'POST',
      body: JSON.stringify({ donor_node_id: donorNodeId }),
    }),

  getFinanceSummary: () => request<FinanceSummary>('/finance/summary'),

  grantSubscription: (payload: { user_id: number; tier: string; days: number }) =>
    request<GrantSubscriptionResponse>('/finance/grant-subscription', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  listPayments: (params: {
    page?: number;
    page_size?: number;
    user_id?: number;
    status?: string;
    tier?: string;
    q?: string;
  }) => request<PaginatedPayments>(`/payments${qs(params)}`),

  getPayment: (paymentId: string) =>
    request<PaymentDetail>(`/payments/${encodeURIComponent(paymentId)}`),

  updatePaymentStatus: (paymentId: string, status: string) =>
    request<PaymentDetail>(`/payments/${encodeURIComponent(paymentId)}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),

  listPromos: () => request<PromoListItem[]>('/promos'),

  getPromo: (code: string) => request<PromoDetail>(`/promos/${encodeURIComponent(code)}`),

  getPromoRedemptions: (code: string, page = 1) =>
    request<PaginatedPromoRedemptions>(
      `/promos/${encodeURIComponent(code)}/redemptions${qs({ page })}`,
    ),

  upsertPromo: (payload: {
    code: string;
    bonus_days: number;
    subscription_tier?: string;
    max_uses?: number;
    active?: boolean;
  }) =>
    request<PromoDetail>('/promos', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  setPromoActive: (code: string, active: boolean) =>
    request<PromoDetail>(`/promos/${encodeURIComponent(code)}/active`, {
      method: 'PATCH',
      body: JSON.stringify({ active }),
    }),

  listTariffs: () => request<TariffListItem[]>('/tariffs'),

  upsertTariff: (payload: {
    tier: string;
    region: string;
    period: string;
    price: number;
    days: number;
  }) =>
    request<TariffDetail>('/tariffs', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  deleteTariff: (tier: string, region: string, period: string) =>
    request<{ ok: boolean }>(
      `/tariffs/${encodeURIComponent(tier)}/${encodeURIComponent(region)}/${encodeURIComponent(period)}`,
      { method: 'DELETE' },
    ),

  listReferralRules: () => request<ReferralRewardRuleItem[]>('/referral-rules'),

  upsertReferralRule: (payload: {
    buyer_subscription_tier: string;
    owner_subscription_tier: string;
    reward_subscription_tier: string;
    reward_days: number;
    enabled?: boolean;
  }) =>
    request<ReferralRewardRuleItem>('/referral-rules', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  updateReferralRule: (
    ruleId: number,
    payload: {
      buyer_subscription_tier: string;
      owner_subscription_tier: string;
      reward_subscription_tier: string;
      reward_days: number;
      enabled?: boolean;
    },
  ) =>
    request<ReferralRewardRuleItem>(`/referral-rules/${ruleId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),

  setReferralRuleEnabled: (ruleId: number, enabled: boolean) =>
    request<ReferralRewardRuleItem>(`/referral-rules/${ruleId}/enabled`, {
      method: 'PATCH',
      body: JSON.stringify({ enabled }),
    }),

  getSettingsOverview: () => request<SettingsOverview>('/settings/overview'),

  listAuditLog: (params: {
    page?: number;
    page_size?: number;
    action?: string;
    actor_telegram_id?: number;
    resource_type?: string;
    q?: string;
  }) => request<PaginatedAuditLog>(`/audit${qs(params)}`),

  listAdmins: () => request<AdminListItem[]>('/admins'),

  upsertAdmin: (payload: { telegram_user_id: number; role: string }) =>
    request<AdminListItem>('/admins', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  updateAdmin: (
    telegramUserId: number,
    payload: { role?: string; is_active?: boolean; display_name?: string | null },
  ) =>
    request<AdminListItem>(`/admins/${telegramUserId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  getMyAdminProfile: () => request<AdminListItem>('/admins/me'),
  updateMyAdminProfile: (payload: { display_name?: string | null }) =>
    request<AdminListItem>('/admins/me', {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  listSupportQuickReplies: () =>
    request<Array<{ id: number; title: string; body: string; owner_telegram_id: number }>>(
      '/support/quick-replies',
    ),
  createSupportQuickReply: (payload: { title: string; body: string; shared?: boolean }) =>
    request<{ id: number; title: string; body: string }>('/support/quick-replies', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  deleteSupportQuickReply: (id: number) =>
    request<{ ok: boolean }>(`/support/quick-replies/${id}`, { method: 'DELETE' }),

  listSupportTickets: (params?: {
    status?: string;
    mine?: boolean;
    page?: number;
    page_size?: number;
  }) =>
    request<PaginatedSupportTickets>(
      `/support/tickets${qs({
        status: params?.status,
        mine: params?.mine ? 'true' : undefined,
        page: params?.page,
        page_size: params?.page_size,
        _: Date.now(),
      })}`,
    ),

  getSupportTicket: (ticketId: number) =>
    request<SupportTicketDetail>(`/support/tickets/${ticketId}?_=${Date.now()}`),

  replySupportTicket: (ticketId: number, body: string) =>
    request<SupportTicketDetail>(`/support/tickets/${ticketId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),

  replySupportTicketMedia: (
    ticketId: number,
    file: Blob | Blob[],
    opts?: {
      caption?: string;
      durationMs?: number;
      asVoice?: boolean;
      asSticker?: boolean;
      asGif?: boolean;
      asVideoNote?: boolean;
      filename?: string;
    },
  ) => {
    const fd = new FormData();
    const list = Array.isArray(file) ? file : [file];
    list.forEach((f, i) => {
      const name =
        opts?.filename && list.length === 1
          ? opts.filename
          : f instanceof File
            ? f.name
            : opts?.asVoice
              ? 'voice.webm'
              : opts?.asVideoNote
                ? 'video-note.webm'
                : `attachment-${i}`;
      fd.append('files', f, name);
    });
    if (opts?.caption) fd.append('caption', opts.caption);
    if (opts?.durationMs != null) fd.append('duration_ms', String(opts.durationMs));
    if (opts?.asVoice) fd.append('as_voice', 'true');
    if (opts?.asSticker) fd.append('as_sticker', 'true');
    if (opts?.asGif) fd.append('as_gif', 'true');
    if (opts?.asVideoNote) fd.append('as_video_note', 'true');
    return request<SupportTicketDetail>(`/support/tickets/${ticketId}/messages/media`, {
      method: 'POST',
      body: fd,
    });
  },

  setSupportReaction: (ticketId: number, messageId: number, emoji: string) =>
    request<SupportTicketDetail>(`/support/tickets/${ticketId}/messages/${messageId}/reactions`, {
      method: 'PUT',
      body: JSON.stringify({ emoji }),
    }),

  supportMediaUrl: (messageId: number, bust?: string | number, index = 0) => {
    const sid = getAdminSessionId();
    const qs = new URLSearchParams();
    if (sid) qs.set('session_id', sid);
    qs.set('v', String(bust ?? messageId));
    if (index > 0) qs.set('index', String(index));
    return `${API_PREFIX}/support/messages/${messageId}/media?${qs.toString()}`;
  },

  editSupportMessage: (messageId: number, body: string) =>
    request<SupportTicketDetail>(`/support/messages/${messageId}`, {
      method: 'PATCH',
      body: JSON.stringify({ body }),
    }),

  deleteSupportMessage: (messageId: number) =>
    request<SupportTicketDetail>(`/support/messages/${messageId}`, { method: 'DELETE' }),

  forwardSupportMessage: (messageId: number) =>
    request<SupportTicketDetail>(`/support/messages/${messageId}/forward`, { method: 'POST' }),

  forwardSupportMessages: (ticketId: number, messageIds: number[], caption = '') =>
    request<SupportTicketDetail>(`/support/tickets/${ticketId}/forward`, {
      method: 'POST',
      body: JSON.stringify({ message_ids: messageIds, caption }),
    }),

  updateSupportTicket: (
    ticketId: number,
    body: { status?: 'open' | 'answered' | 'closed'; claim?: boolean },
  ) =>
    request<SupportTicketDetail>(`/support/tickets/${ticketId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  getSupportStorageStats: () => request<SupportStorageStats>('/support/storage/stats'),

  listSupportStorageFiles: (params?: {
    page?: number;
    page_size?: number;
    kind?: string;
    older_than_days?: number;
  }) => request<PaginatedSupportStorageFiles>(`/support/storage/files${qs(params ?? {})}`),

  cleanupSupportStorage: (body: {
    keys?: string[];
    older_than_days?: number;
    kind?: string;
  }) =>
    request<SupportStorageDeleteResult>('/support/storage/cleanup', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  supportStorageFileUrl: (key: string) => {
    const sid = getAdminSessionId();
    const q = new URLSearchParams();
    q.set('key', key);
    if (sid) q.set('session_id', sid);
    return `${API_PREFIX}/support/storage/file?${q.toString()}`;
  },

  getPushVapidKey: () => request<{ public_key: string }>('/support/push/vapid-key'),

  subscribePush: (body: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
    request<{ subscribed: boolean }>('/support/push/subscribe', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  unsubscribePush: (endpoint: string) =>
    request<{ unsubscribed: boolean }>('/support/push/subscribe', {
      method: 'DELETE',
      body: JSON.stringify({ endpoint }),
    }),
};
